import dayjs from 'dayjs'
import * as _ from 'lodash-es'
import PropTypes from 'prop-types'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Textarea } from '@/components/forms/Textarea'
import { PaperAirplaneIcon } from '@/components/icons'
import { Button } from '@/components/ui/Button'
import { useWebSocket } from '@/hooks/Websocket'
import { createComment, getComments, markCommentsRead } from '@/services/repairOrders.service'
import { UserStoreContext } from '@/stores/UserStore'
import { track } from '@/utils/analytics'
import { COMMENT_SENT, COMMENTS_READ, CHAT_OPENED } from '@/utils/analytics.constants'
import { FOOTER_HEIGHT } from '@/utils/constants'
import { toast } from '@/utils/helpers'

const RepairOrderChat = ({ dealerId, repairOrderId, isRepairOrderResolved, onCommentsRead }) => {
  const [chatComments, setChatComments] = useState([])
  const [nextCursor, setNextCursor] = useState(null)
  const [loading, setLoading] = useState(false)
  const [shouldChatScroll, setShouldChatScroll] = useState(true)
  const { accessToken, isImpersonating, user } = useContext(UserStoreContext)
  const commentsRef = useRef(null)
  const handleError = (message) => toast(message, 'error')

  let lastDay = null

  const { handleSubmit, getValues, register, reset } = useForm({
    defaultValues: {
      message: '',
    },
  })

  /**
   * Set up the WebSocket connection to listen for new comments
   */
  const handleMessage = useCallback((msg) => {
    const newComment = msg.data
    setChatComments((prev) => {
      // Deduplicate: skip if a comment with the same ID already exists
      if (prev.some((c) => c.id === newComment.id)) {
        return prev
      }
      return [...prev, newComment]
    })
  }, [])

  const websocketStatus = useWebSocket({
    path: `v1/repair_orders/${repairOrderId}`,
    onMessage: handleMessage,
    enabled: repairOrderId !== null,
    token: accessToken,
    config: {
      debug: true,
    },
  })

  /**
   * Scroll to the last comment when chatComments change and shouldChatScroll is true.
   */
  useEffect(() => {
    if (chatComments.length > 0 && shouldChatScroll) {
      const lastCommentElement = commentsRef.current.lastChild
      lastCommentElement?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }, [chatComments])

  useEffect(() => {
    track(CHAT_OPENED, { dealer_id: dealerId, ro_id: repairOrderId })
    loadMessages()
    // Mark comments as read when the chat is opened
    const markAsRead = async () => {
      const success = await markCommentsRead(dealerId, repairOrderId)
      if (success && onCommentsRead) {
        track(COMMENTS_READ, { dealer_id: dealerId, ro_id: repairOrderId })
        onCommentsRead()
      }
    }
    markAsRead()
  }, [dealerId, repairOrderId])

  const loadMessages = async () => {
    await getComments(dealerId, repairOrderId, nextCursor, setLoading, handleError, (response) => {
      // Reverse the comments so the latest one is at the bottom
      setChatComments((prevMessages) => [..._.reverse(response.results), ...prevMessages])

      // Set the next cursor if it exists
      if (response.next) {
        const url = new URL(response.next)
        setNextCursor(url.searchParams.get('cursor'))
      } else {
        setNextCursor(null)
      }
    })
  }

  const handleLoadMore = () => {
    if (nextCursor && !loading) {
      loadMessages()
      setShouldChatScroll(false)
    }
  }

  /**
   * Handle form submission for new comment.
   * @param {object} data - The form data.
   */
  const onSubmit = async ({ message }) => {
    if (message.trim()) {
      await createComment(
        dealerId,
        repairOrderId,
        { message },
        setLoading,
        (errorMessage) => {
          handleError(errorMessage)
        },
        (response) => {
          setChatComments((prev) => {
            if (prev.some((c) => c.id === response.id)) {
              return prev
            }
            return [...prev, response]
          })
          track(COMMENT_SENT, {
            dealer_id: dealerId,
            ro_id: repairOrderId,
            message_length: message.trim().length,
          })
          setShouldChatScroll(true)
          reset()
        },
      )
    }
  }

  /**
   * Render user name or "You" if the comment is by the logged-in user.
   * @param {object} commentUser - The user who made the comment.
   * @returns {string} - The user's name or "You".
   */
  const renderUserName = (commentUser) => {
    if (commentUser.id === user.id) {
      return 'You'
    }
    return commentUser.displayName
  }

  const renderCommentText = (comment) => {
    if (comment.status === 'sending') {
      return {
        visibleText: 'Sending...',
        screenReaderText: `${renderUserName(comment.user)} is sending a message.`,
      }
    }
    if (comment.status === 'error') {
      return {
        visibleText: 'Failed to send',
        screenReaderText: `${renderUserName(comment.user)} tried to send a message but it failed.`,
      }
    }
    const formattedTime = dayjs(comment.createdAt).format('h:mm A')
    return {
      visibleText: `${renderUserName(comment.user)} - ${formattedTime}`,
      screenReaderText: `${renderUserName(comment.user)} commented at ${formattedTime}:`,
    }
  }

  return (
    <aside
      className="bg-midnight-950/90 relative ml-0 h-full md:min-w-[489px]"
      data-testid="chatContainer"
    >
      <div className="absolute inset-y-0 right-0 flex w-full grow flex-col">
        {!websocketStatus.isConnected && (
          <div className="bg-midnight-950 w-full p-2">
            {!websocketStatus.isMaxedOut && (
              <p className="text-charcoal-light text-center text-sm">Connecting...</p>
            )}
            {websocketStatus.isMaxedOut && (
              <p className="text-charcoal-light text-center text-sm">
                Could not connect to live messages. Try refreshing your page.
              </p>
            )}
          </div>
        )}

        <div className="grow overflow-y-auto p-4" id="chatContainer">
          {websocketStatus.isConnected && chatComments.length === 0 && (
            <p className="text-charcoal-light mb-8 text-center text-sm">No messages.</p>
          )}
          {nextCursor && (
            <Button
              type="button"
              onClick={handleLoadMore}
              loading={loading}
              label="Show Older Mesages"
              outlined
              background="bg-white"
              size="xs"
              className="m-auto mb-4 text-center "
            />
          )}
          <ol className="flex flex-col" ref={commentsRef}>
            {chatComments.map((comment) => {
              const currentDay = dayjs(comment.createdAt).format('ddd, MMM D')
              let dayChanged = false
              if (lastDay !== currentDay) {
                lastDay = currentDay
                dayChanged = true
              }
              return (
                <React.Fragment key={comment.id}>
                  {dayChanged && (
                    <li className="text-charcoal-light mb-8 text-center text-xs">
                      {dayjs(comment.createdAt).format('ddd, MMM D h:mm A')}
                    </li>
                  )}

                  <li
                    className={`pb-6 ${
                      comment.user.id === user.id ? 'self-end text-right' : 'self-start text-left'
                    }`}
                  >
                    <div
                      className={`mb-2 w-[271px] break-words p-2 text-sm ${
                        comment.user.id === user.id
                          ? 'self-start rounded-b-md rounded-tl-md bg-white text-right text-black'
                          : 'self-end rounded-t-md rounded-br-md bg-blue-500 text-white'
                      }`}
                    >
                      <p className="sr-only">{renderCommentText(comment).screenReaderText}</p>
                      <p>{comment.message}</p>
                    </div>

                    <p
                      className={`text-charcoal-light text-xs ${
                        comment.user.id === user.id ? 'text-right' : ''
                      }`}
                      aria-hidden="true"
                    >
                      {renderCommentText(comment).visibleText}
                    </p>
                  </li>
                </React.Fragment>
              )
            })}
          </ol>
        </div>

        <div
          className="bg-midnight-950 w-full content-center p-4"
          style={{ height: `${FOOTER_HEIGHT}px` }}
        >
          {isRepairOrderResolved || isImpersonating ? (
            <div className="flex items-center justify-center">
              <p className="text-md text-white">
                {isImpersonating
                  ? 'Chat is disabled while you are viewing as another user.'
                  : 'Chat is disabled because repair order is resolved.'}
              </p>
            </div>
          ) : (
            <form className="flex w-full items-center" onSubmit={handleSubmit(onSubmit)}>
              <Textarea
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.metaKey) {
                    e.preventDefault()
                    onSubmit(getValues())
                  }
                }}
                disabled={loading}
                placeholder="Message"
                name="message"
                fullWidth
                style={{ resize: 'none' }}
                {...register('message', { required: true })}
              />
              <button
                aria-label="Send message"
                type="submit"
                disabled={loading}
                className="ml-2 flex h-12 items-center justify-center rounded-lg bg-blue-800 px-4 text-white disabled:opacity-50"
              >
                <PaperAirplaneIcon className="h-4 text-white" />
              </button>
            </form>
          )}
        </div>
      </div>
    </aside>
  )
}

RepairOrderChat.propTypes = {
  dealerId: PropTypes.string.isRequired,
  repairOrderId: PropTypes.string.isRequired,
  isRepairOrderResolved: PropTypes.bool.isRequired,
  onCommentsRead: PropTypes.func,
}

export default RepairOrderChat
